export type EmailRecipient = { email: string; name?: string; id?: string };

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isRecipientEmail(email: string): boolean {
  return email.length <= 254 && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email);
}

export function parseRecipientEmails(text: string): string[] {
  return (text.match(/[^,;\n]*<[^>]+>|[^\s,;]+/g) ?? []).map((part) =>
    normalizeEmail(part.match(/<([^>]+)>/)?.[1] ?? part),
  ).filter(Boolean);
}

export function addRecipientEmails(
  value: EmailRecipient[], text: string, suggestions: EmailRecipient[], maximum: number,
) {
  const recipients = value.map((person) => ({
    ...suggestions.find((known) => normalizeEmail(known.email) === normalizeEmail(person.email)),
    ...person, email: normalizeEmail(person.email),
  }));
  const invalid: string[] = [];
  const overflow: string[] = [];
  for (const email of parseRecipientEmails(text)) {
    if (!isRecipientEmail(email)) { invalid.push(email); continue; }
    const known = suggestions.find((person) => normalizeEmail(person.email) === email);
    if (recipients.some((person) => normalizeEmail(person.email) === email || (known?.id && person.id === known.id))) continue;
    if (recipients.length >= maximum) { overflow.push(email); continue; }
    recipients.push({ ...known, email });
  }
  return { recipients, invalid, overflow };
}

export function recipientSuggestions(value: EmailRecipient[], suggestions: EmailRecipient[], search: string) {
  const fold = (text: string) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const query = fold(search.trim());
  const seen = new Set(value.map((person) => normalizeEmail(person.email)));
  const selectedIds = new Set(value.map((person) => person.id).filter(Boolean));
  return suggestions.filter((person) => {
    const email = normalizeEmail(person.email);
    if (!isRecipientEmail(email) || seen.has(email) || (person.id && selectedIds.has(person.id))) return false;
    seen.add(email);
    return fold(`${person.name ?? ""} ${email}`).includes(query);
  }).slice(0, 8);
}
